import React from "react";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";
const css = `
:root{
  --ps-bg:
    radial-gradient(1200px 600px at 80% 20%, rgba(129,161,255,.22), transparent 60%),
    radial-gradient(900px 500px at 10% 80%, rgba(255,170,210,.18), transparent 60%),
    linear-gradient(180deg, #ffffff, #fbfbff 40%, #ffffff 100%);
  --ps-black:#0b0b0c;
  --ps-muted:#6b7280;
  --ps-accent:#6E5BFF;
  --ps-accent-2:#A78BFA;
  --ps-line:#eaeaef;
  --ps-card:#fff;
  --nav-h: 56px;
  --nav-border: rgba(17, 24, 39, .08);
}

/* Layout + scrolling */
.page-scroll {
  height: calc(100vh - var(--nav-h, 56px));
  overflow: auto;
  scroll-behavior: smooth;
}
@media (max-width: 640px) {
  .page-scroll {
    height: calc(100svh - var(--nav-h, 56px));
  }
}
.page-scroll {
  scrollbar-width: thin;
  scrollbar-color: #a78bfa #eef1ff;
}
.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track {
  background:#eef1ff;
  border-left:1px solid var(--nav-border, rgba(17,24,39,.08));
}
.page-scroll::-webkit-scrollbar-thumb {
  background:linear-gradient(180deg,#c7d2fe,#a78bfa);
  border:2px solid #eef1ff;
  border-radius:8px;
}

html, body { height: 100%; overflow-y: hidden !important; }
body { overflow-y: hidden !important; }

.ps-wrap{ background:var(--ps-bg); min-height:100svh; }
@supports (min-height: 100dvh){
  .ps-wrap{ min-height:100dvh; }
}
.ps-container{
  max-width:900px;
  margin:0 auto;
  padding:56px 24px 40px;
}

/* Legal page */
.legal-page {
  background: rgba(255,255,255,0.9);
  border-radius: 24px;
  border: 1px solid rgba(148,163,184,0.4);
  padding: 28px 24px 26px;
  box-shadow: 0 18px 45px rgba(15,23,42,0.08);
}
@media (min-width: 900px){
  .legal-page {
    padding: 32px 32px 30px;
  }
}
.legal-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 18px;
  margin-bottom: 18px;
}
.legal-eyebrow {
  display:inline-flex;
  align-items:center;
  gap:8px;
  font-size:11px;
  font-weight:700;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:#6b7280;
}
.legal-dot {
  width:6px; height:6px; border-radius:999px;
  background:linear-gradient(135deg,var(--ps-accent),var(--ps-accent-2));
}
.legal-title {
  margin:10px 0 4px;
  font-size:28px;
  line-height:1.25;
  letter-spacing:-.02em;
  color:var(--ps-black);
}
.legal-meta {
  font-size:13px;
  color:#9ca3af;
}
.legal-meta strong {
  font-weight:600;
  color:#6b7280;
}

.legal-content {
  font-size:14px;
  color:#374151;
  line-height:1.7;
}
.legal-content p {
  margin:0 0 12px;
}
.legal-section {
  margin-top:22px;
}
.legal-section h2 {
  font-size:18px;
  margin:0 0 6px;
  color:var(--ps-black);
}
.legal-section h3 {
  font-size:15px;
  margin:14px 0 4px;
  color:var(--ps-black);
}
.legal-section ul {
  margin:0 0 12px 1.1rem;
  padding:0;
}
.legal-section li {
  margin-bottom:4px;
}
.legal-small {
  font-size:12px;
  color:#9ca3af;
  margin-top:18px;
}

/* Footer (simple) */
.legal-footer {
  margin-top:20px;
  padding-top:16px;
  border-top:1px solid rgba(148,163,184,0.4);
  display:flex;
  flex-wrap:wrap;
  gap:10px;
  justify-content:space-between;
  align-items:center;
  font-size:12px;
  color:#9ca3af;
}
.legal-footer a {
  color:#6b7280;
  text-decoration:none;
}
.legal-footer a:hover {
  color:var(--ps-black);
  text-decoration:underline;
}

@media (max-width: 640px){
  .ps-container{
    padding-inline:16px;
  }
  .legal-page{
    border-radius:20px;
    padding-inline:18px;
  }
}
`;

export default function Terms() {
  const seo = seoPresets["/terms"];
  return (
     <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
    <div className="ps-wrap">
      <style>{css}</style>
      <div className="page-scroll">
        <main className="ps-container">
          <article className="legal-page">
            <header className="legal-header">
              <div className="legal-eyebrow">
                <span className="legal-dot" aria-hidden="true" />
                <span>Legal</span>
              </div>
              <h1 className="legal-title">Terms of Service</h1>
              <p className="legal-meta">
                <strong>Last updated:</strong> 24 November 2025
              </p>
            </header>

            <section className="legal-content">
              <p>
                These Terms of Service (&quot;Terms&quot;) govern your access to and use of MusicBase
                (&quot;MusicBase&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) and the MusicBase web
                application, website, and related services (collectively, the &quot;Service&quot;).
              </p>
              <p>
                By creating an account, accessing, or using the Service in any way, you agree to be bound by these Terms.
                If you do not agree, do not use the Service.
              </p>

              <div className="legal-section" id="eligibility">
                <h2>1. Eligibility</h2>
                <p>
                  You may use the Service only if you are at least 16 years old (or the age of digital consent in your
                  country) and able to enter into a binding contract with us. If you use the Service on behalf of an
                  organization, you represent that you have authority to bind that organization to these Terms.
                </p>
              </div>

              <div className="legal-section" id="account">
                <h2>2. Account registration</h2>
                <p>
                  To use most features, you must create an account and provide accurate, current information, including
                  a valid email address. You are responsible for:
                </p>
                <ul>
                  <li>Maintaining the confidentiality of your login credentials.</li>
                  <li>All activity that occurs under your account.</li>
                </ul>
                <p>
                  You must notify us immediately if you suspect unauthorized access to your account. We are not liable
                  for losses caused by your failure to safeguard your account.
                </p>
              </div>

              <div className="legal-section" id="subscriptions">
                <h2>3. Subscriptions, billing, and credits</h2>
                <h3>3.1 Plans and billing</h3>
                <p>
                  Some parts of the Service are free, while others require a paid subscription. When you subscribe to a
                  paid plan, you authorize us and our payment processor (Stripe) to charge you the subscription fees and
                  any applicable taxes on a recurring basis (for example, monthly), until you cancel.
                </p>
                <p>
                  Prices, features, and limits for each plan are described in the Service and may change over time. If
                  we change prices for an existing subscription, we will provide notice, and the new price will apply
                  from your next billing cycle or as otherwise stated in the notice.
                </p>

                <h3>3.2 Renewal and cancellation</h3>
                <p>
                  Subscriptions automatically renew at the end of each billing period unless you cancel from inside the
                  app or through your app store / payment method, where applicable, before the renewal date.
                </p>
                <p>
                  You can cancel at any time; when you cancel, your subscription will typically remain active until the
                  end of the current billing period. Fees already paid are generally non-refundable unless required by
                  applicable law.
                </p>

                <h3>3.3 Usage limits and credits</h3>
                <p>
                  Your plan may include limits such as searches, campaigns, or credits per month. Details are shown
                  inside the app. We may reset or expire unused credits at the end of each billing period unless stated
                  otherwise. We may also adjust limits or credit systems over time to improve the Service.
                </p>
              </div>

              <div className="legal-section" id="use-of-service">
                <h2>4. Use of the Service</h2>
                <h3>4.1 Acceptable use</h3>
                <p>You agree not to misuse the Service. For example, you must not:</p>
                <ul>
                  <li>Use the Service for unlawful, harmful, or fraudulent purposes.</li>
                  <li>
                    Send spam or unsolicited messages in violation of anti-spam laws (such as CAN-SPAM, GDPR, or
                    similar laws in your jurisdiction).
                  </li>
                  <li>
                    Harass, threaten, or mislead playlist curators or other recipients, or impersonate another person
                    or entity.
                  </li>
                  <li>
                    Attempt to gain unauthorized access to any systems, networks, or data, or interfere with the
                    security or integrity of the Service.
                  </li>
                  <li>Reverse engineer, decompile, or otherwise attempt to extract source code from the Service.</li>
                  <li>
                    Use bots, automated scripts, or scraping methods that violate third-party terms (including Spotify&apos;s
                    terms) or applicable law.
                  </li>
                </ul>

                <h3>4.2 No guarantees of placement</h3>
                <p>
                  MusicBase helps you discover playlists and manage outreach. We do not control playlist curators and
                  cannot guarantee placements, responses, streams, or any particular outcome from your campaigns.
                </p>
              </div>

              <div className="legal-section" id="gmail-integrations">
                <h2>5. Gmail and third-party integrations</h2>
                <p>
                  Some features require connecting third-party accounts (for example, Google / Gmail) via OAuth or
                  similar mechanisms. When you connect a third-party account, you authorize us to access and use that
                  account&apos;s data as described in the Service and in our Privacy Policy.
                </p>
                <p>
                  In particular, when you connect Gmail, we may send emails from your account, read limited message
                  metadata and snippets to detect replies, and track engagement related to your campaigns, as further
                  detailed in our Privacy Policy.
                </p>
                <p>
                  Your use of any third-party service (such as Google, Spotify, Instagram) is also governed by that
                  service&apos;s own terms and policies. You are responsible for complying with those terms, and we are
                  not responsible for third-party services or their behavior.
                </p>
              </div>

              <div className="legal-section" id="content">
                <h2>6. Your content and our rights</h2>
                <h3>6.1 Your content</h3>
                <p>
                  You may submit or upload content to the Service, including messages, templates, notes, links, and
                  other materials (&quot;Your Content&quot;). You retain any ownership rights you have in Your Content.
                </p>
                <p>
                  By using the Service, you grant us a worldwide, non-exclusive, royalty-free license to host, store,
                  process, and display Your Content as needed to operate and improve the Service and as otherwise
                  described in these Terms and our Privacy Policy.
                </p>

                <h3>6.2 Responsibility for content</h3>
                <p>
                  You are solely responsible for Your Content and for ensuring that it complies with these Terms and
                  applicable law (including rights of third parties such as copyright, trademark, and privacy rights).
                </p>
              </div>

              <div className="legal-section" id="ip">
                <h2>7. Intellectual property</h2>
                <p>
                  The Service, including all software, interfaces, designs, branding, and content we provide (other than
                  Your Content), is owned by or licensed to MusicBase and is protected by intellectual property laws.
                </p>
                <p>
                  Subject to your compliance with these Terms and payment of any applicable fees, we grant you a
                  limited, non-exclusive, non-transferable, revocable license to access and use the Service for your own
                  internal business or personal artist use.
                </p>
                <p>You may not copy, modify, distribute, sell, or lease any part of the Service except as allowed here.</p>
              </div>

              <div className="legal-section" id="privacy">
                <h2>8. Privacy</h2>
                <p>
                  Our collection and use of personal data in connection with the Service is described in our{" "}
                  <a href="/privacy">Privacy Policy</a>. By using the Service, you also agree to our Privacy Policy.
                </p>
              </div>

              <div className="legal-section" id="termination">
                <h2>9. Termination and suspension</h2>
                <p>We may suspend or terminate your access to the Service, with or without notice, if we believe that:</p>
                <ul>
                  <li>You have materially breached these Terms or applicable law.</li>
                  <li>Your use of the Service poses a security or legal risk.</li>
                  <li>We are required to do so by law or by a third-party service provider.</li>
                </ul>
                <p>
                  You may stop using the Service at any time and may cancel any active subscriptions as described in
                  Section 3. Upon termination or expiration, your right to use the Service will end, but certain
                  provisions of these Terms (including, for example, disclaimers and limitations of liability) will
                  continue to apply.
                </p>
              </div>

              <div className="legal-section" id="disclaimers">
                <h2>10. Disclaimers</h2>
                <p>
                  The Service is provided on an &quot;as is&quot; and &quot;as available&quot; basis, without warranties
                  of any kind, whether express, implied, or statutory, including any implied warranties of
                  merchantability, fitness for a particular purpose, or non-infringement.
                </p>
                <p>
                  We do not warrant that the Service will be uninterrupted, error-free, secure, or that it will produce
                  any specific results (such as playlist placements, increased streams, or revenue).
                </p>
              </div>

              <div className="legal-section" id="liability">
                <h2>11. Limitation of liability</h2>
                <p>
                  To the maximum extent permitted by law, MusicBase and its owners, employees, and affiliates will not
                  be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of
                  profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or
                  other intangible losses, resulting from:
                </p>
                <ul>
                  <li>Your access to or use of, or inability to access or use, the Service.</li>
                  <li>Any conduct or content of any third party using the Service.</li>
                  <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
                </ul>
                <p>
                  To the maximum extent permitted by law, our total liability for any claim arising out of or relating
                  to the Service or these Terms will be limited to the amount you paid to use the Service in the
                  3-month period immediately preceding the event giving rise to the claim, or, if you have not paid
                  anything, EUR 50.
                </p>
                <p>
                  Some jurisdictions do not allow the exclusion or limitation of certain damages, so the above
                  limitations may not fully apply to you.
                </p>
              </div>

              <div className="legal-section" id="indemnity">
                <h2>12. Indemnity</h2>
                <p>
                  To the extent permitted by law, you agree to indemnify and hold harmless MusicBase and its owners,
                  employees, and affiliates from and against any claims, damages, losses, liabilities, and expenses
                  (including reasonable legal fees) arising out of or related to:
                </p>
                <ul>
                  <li>Your use of the Service.</li>
                  <li>Your Content.</li>
                  <li>Your violation of these Terms or applicable law.</li>
                </ul>
              </div>

              <div className="legal-section" id="changes">
                <h2>13. Changes to the Service and these Terms</h2>
                <p>
                  We are always working to improve MusicBase and may add, change, or remove features from time to time.
                  We may also update these Terms occasionally. When we do, we will revise the &quot;Last updated&quot;
                  date above and may provide additional notice (such as in-app or by email) for material changes.
                </p>
                <p>
                  If you continue using the Service after the revised Terms become effective, you agree to the updated
                  Terms. If you do not agree, you must stop using the Service.
                </p>
              </div>

              <div className="legal-section" id="misc">
                <h2>14. Miscellaneous</h2>
                <p>
                  If any part of these Terms is found to be invalid or unenforceable, that part will be enforced to the
                  maximum extent permitted, and the remaining provisions will remain in full force and effect.
                </p>
                <p>
                  You may not assign or transfer these Terms or your rights under them without our prior written
                  consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets, or by
                  operation of law.
                </p>
                <p>
                  These Terms, together with our Privacy Policy, form the entire agreement between you and MusicBase
                  regarding the Service.
                </p>
                <p>
                  The laws that apply and the courts that have jurisdiction may depend on where you live and where
                  MusicBase is established. You should adapt this section to your preferred governing law and venue if
                  you seek legal advice.
                </p>
              </div>

              <p className="legal-small">
                This document is a general template and does not constitute legal advice. You should consult a qualified
                lawyer to review and adapt these Terms to your specific business, jurisdiction, and use case.
              </p>
            </section>

            <footer className="legal-footer">
              <span>© {new Date().getFullYear()} MusicBase</span>
              <span>
                <a href="/">Home</a> · <a href="/privacy">Privacy</a>
              </span>
            </footer>
          </article>
        </main>
      </div>
    </div>
    </>
  );
}
